// format.ts — the web client's codec for openscan's binary splat formats
// (ARCHITECTURE.md §5): full OSPL snapshots and OSPD deltas. It must agree
// with core/splat byte for byte, so every offset below is spelled out against
// the 64-byte record layout:
//
//   pos[3] f32 | log_scale[3] f32 | rot[4] f32 (w,x,y,z) | color[3] f32 |
//   opacity f32 | label u32 | id u32                       (little-endian)
//
// OSPL v2 (§16.5; header version 2, flags bit 0 = PBR) appends a material
// block to each record, 96 bytes in all — bytes 0..63 are exactly the v1
// record (color stays the captured sRGB display colour):
//
//   albedo[3] f32 (linear) @64 | ao f32 @76 | roughness f32 @80 |
//   specular f32 @84 | normal_oct[2] f32 @88,92
//
// OSPD deltas stay v1.
//
// Clouds are held struct-of-arrays (one typed array per field) because the
// renderer, the depth sort and the picker all stream over single fields.

export const LABEL_UNKNOWN = 0;
export const LABEL_BACKGROUND = 1;
export const LABEL_OBJECT = 2;
export const LABEL_PLANE = 3;

export const RECORD_BYTES = 64;
export const OSPL_HEADER_BYTES = 16;
export const OSPL_MAGIC = 'OSPL';
export const OSPD_MAGIC = 'OSPD';
export const FORMAT_VERSION = 1;
export const FORMAT_VERSION_PBR = 2;
export const RECORD_BYTES_PBR = 96;
export const FLAG_PBR = 1;

export class FormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormatError';
  }
}

// SplatCloud is a struct-of-arrays gaussian cloud. All arrays have exactly
// `count` (×arity) entries.
export interface SplatCloud {
  count: number;
  ids: Uint32Array;
  pos: Float32Array; // 3·count
  logScale: Float32Array; // 3·count
  rot: Float32Array; // 4·count, w,x,y,z
  color: Float32Array; // 3·count, sRGB 0..1
  opacity: Float32Array; // count
  label: Uint32Array; // count
  material?: SplatMaterial; // OSPL v2 only (§16.5)
}

// SplatMaterial is the v2 per-gaussian material, struct-of-arrays.
export interface SplatMaterial {
  albedo: Float32Array; // 3·count, linear 0..1
  ao: Float32Array; // count
  roughness: Float32Array; // count
  specular: Float32Array; // count
  normal: Float32Array; // 3·count, unit (decoded from octahedral)
}

export function emptyMaterial(count: number): SplatMaterial {
  return {
    albedo: new Float32Array(count * 3),
    ao: new Float32Array(count),
    roughness: new Float32Array(count),
    specular: new Float32Array(count),
    normal: new Float32Array(count * 3),
  };
}

const sgn = (v: number): number => (v >= 0 ? 1 : -1);

// octEncode maps a unit vector to the octahedral square [-1,1]².
export function octEncode(x: number, y: number, z: number): [number, number] {
  const l1 = Math.abs(x) + Math.abs(y) + Math.abs(z);
  if (l1 === 0) return [0, 0];
  let u = x / l1, v = y / l1;
  if (z / l1 < 0) [u, v] = [(1 - Math.abs(v)) * sgn(u), (1 - Math.abs(u)) * sgn(v)];
  return [u, v];
}

// octDecode is the inverse of octEncode (normalized).
export function octDecode(u: number, v: number): [number, number, number] {
  const z = 1 - Math.abs(u) - Math.abs(v);
  let x = u, y = v;
  if (z < 0) [x, y] = [(1 - Math.abs(v)) * sgn(u), (1 - Math.abs(u)) * sgn(v)];
  const n = Math.hypot(x, y, z) || 1;
  return [x / n, y / n, z / n];
}

export interface SplatDelta {
  updated: SplatCloud;
  removed: Uint32Array;
}

// Gaussian is the array-of-structs view of one record (tests, small edits).
export interface Gaussian {
  id: number;
  pos: [number, number, number];
  logScale: [number, number, number];
  rot: [number, number, number, number];
  color: [number, number, number];
  opacity: number;
  label: number;
}

export function emptyCloud(count = 0): SplatCloud {
  return {
    count,
    ids: new Uint32Array(count),
    pos: new Float32Array(count * 3),
    logScale: new Float32Array(count * 3),
    rot: new Float32Array(count * 4),
    color: new Float32Array(count * 3),
    opacity: new Float32Array(count),
    label: new Uint32Array(count),
  };
}

function asView(buf: ArrayBuffer | ArrayBufferView): DataView {
  if (buf instanceof ArrayBuffer) return new DataView(buf);
  return new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
}

function readMagic(dv: DataView, off: number): string {
  return String.fromCharCode(dv.getUint8(off), dv.getUint8(off + 1), dv.getUint8(off + 2), dv.getUint8(off + 3));
}

function writeMagic(dv: DataView, off: number, magic: string): void {
  for (let i = 0; i < 4; i++) dv.setUint8(off + i, magic.charCodeAt(i));
}

// readRecords decodes `n` consecutive records of `stride` bytes starting at
// `off` (64: v1; 96: v2, whose material block is read too).
function readRecords(dv: DataView, off: number, n: number, stride = RECORD_BYTES): SplatCloud {
  const c = emptyCloud(n);
  const m = stride === RECORD_BYTES_PBR ? emptyMaterial(n) : null;
  if (m) c.material = m;
  for (let i = 0; i < n; i++) {
    const b = off + i * stride;
    for (let k = 0; k < 3; k++) c.pos[i * 3 + k] = dv.getFloat32(b + k * 4, true);
    for (let k = 0; k < 3; k++) c.logScale[i * 3 + k] = dv.getFloat32(b + 12 + k * 4, true);
    for (let k = 0; k < 4; k++) c.rot[i * 4 + k] = dv.getFloat32(b + 24 + k * 4, true);
    for (let k = 0; k < 3; k++) c.color[i * 3 + k] = dv.getFloat32(b + 40 + k * 4, true);
    c.opacity[i] = dv.getFloat32(b + 52, true);
    c.label[i] = dv.getUint32(b + 56, true);
    c.ids[i] = dv.getUint32(b + 60, true);
    if (m) {
      for (let k = 0; k < 3; k++) m.albedo[i * 3 + k] = dv.getFloat32(b + 64 + k * 4, true);
      m.ao[i] = dv.getFloat32(b + 76, true);
      m.roughness[i] = dv.getFloat32(b + 80, true);
      m.specular[i] = dv.getFloat32(b + 84, true);
      m.normal.set(octDecode(dv.getFloat32(b + 88, true), dv.getFloat32(b + 92, true)), i * 3);
    }
  }
  return c;
}

function writeRecords(dv: DataView, off: number, c: SplatCloud, stride = RECORD_BYTES): void {
  const m = stride === RECORD_BYTES_PBR ? c.material : undefined;
  for (let i = 0; i < c.count; i++) {
    const b = off + i * stride;
    for (let k = 0; k < 3; k++) dv.setFloat32(b + k * 4, c.pos[i * 3 + k], true);
    for (let k = 0; k < 3; k++) dv.setFloat32(b + 12 + k * 4, c.logScale[i * 3 + k], true);
    for (let k = 0; k < 4; k++) dv.setFloat32(b + 24 + k * 4, c.rot[i * 4 + k], true);
    for (let k = 0; k < 3; k++) dv.setFloat32(b + 40 + k * 4, c.color[i * 3 + k], true);
    dv.setFloat32(b + 52, c.opacity[i], true);
    dv.setUint32(b + 56, c.label[i], true);
    dv.setUint32(b + 60, c.ids[i], true);
    if (m) {
      for (let k = 0; k < 3; k++) dv.setFloat32(b + 64 + k * 4, m.albedo[i * 3 + k], true);
      dv.setFloat32(b + 76, m.ao[i], true);
      dv.setFloat32(b + 80, m.roughness[i], true);
      dv.setFloat32(b + 84, m.specular[i], true);
      const [u, v] = octEncode(m.normal[i * 3], m.normal[i * 3 + 1], m.normal[i * 3 + 2]);
      dv.setFloat32(b + 88, u, true);
      dv.setFloat32(b + 92, v, true);
    }
  }
}

// parseOSPL decodes a full OSPL snapshot, v1 or v2 (v2 sets `material`).
export function parseOSPL(buf: ArrayBuffer | ArrayBufferView): SplatCloud {
  const dv = asView(buf);
  if (dv.byteLength < OSPL_HEADER_BYTES) throw new FormatError(`OSPL: ${dv.byteLength} bytes is shorter than the header`);
  const magic = readMagic(dv, 0);
  if (magic !== OSPL_MAGIC) throw new FormatError(`OSPL: bad magic ${JSON.stringify(magic)}`);
  const version = dv.getUint32(4, true);
  if (version !== FORMAT_VERSION && version !== FORMAT_VERSION_PBR) throw new FormatError(`OSPL: unsupported version ${version}`);
  const count = dv.getUint32(8, true);
  const stride = version === FORMAT_VERSION_PBR ? RECORD_BYTES_PBR : RECORD_BYTES;
  const need = OSPL_HEADER_BYTES + count * stride;
  if (dv.byteLength < need) throw new FormatError(`OSPL v${version}: truncated, ${dv.byteLength} < ${need} bytes for ${count} gaussians`);
  if (version === FORMAT_VERSION_PBR) {
    const flags = dv.getUint32(12, true);
    if (!(flags & FLAG_PBR)) console.warn(`[openscan/format] OSPL v2 without the PBR flag (flags=${flags}); reading the material anyway`);
  }
  return readRecords(dv, OSPL_HEADER_BYTES, count, stride);
}

// encodeOSPL is the inverse of parseOSPL: v2 (flags = PBR) when the cloud has
// a material, else v1 with flags 0, exactly as before v2 existed.
export function encodeOSPL(c: SplatCloud): Uint8Array {
  const pbr = !!c.material;
  const stride = pbr ? RECORD_BYTES_PBR : RECORD_BYTES;
  const out = new Uint8Array(OSPL_HEADER_BYTES + c.count * stride);
  const dv = new DataView(out.buffer);
  writeMagic(dv, 0, OSPL_MAGIC);
  dv.setUint32(4, pbr ? FORMAT_VERSION_PBR : FORMAT_VERSION, true);
  dv.setUint32(8, c.count, true);
  dv.setUint32(12, pbr ? FLAG_PBR : 0, true);
  writeRecords(dv, OSPL_HEADER_BYTES, c, stride);
  return out;
}

// parseOSPD decodes a delta: "OSPD" | ver | nUpd | nUpd×64 | nRem | nRem×u32.
export function parseOSPD(buf: ArrayBuffer | ArrayBufferView): SplatDelta {
  const dv = asView(buf);
  if (dv.byteLength < 12) throw new FormatError(`OSPD: ${dv.byteLength} bytes is shorter than the header`);
  const magic = readMagic(dv, 0);
  if (magic !== OSPD_MAGIC) throw new FormatError(`OSPD: bad magic ${JSON.stringify(magic)}`);
  const version = dv.getUint32(4, true);
  if (version !== FORMAT_VERSION) throw new FormatError(`OSPD: unsupported version ${version}`);
  const nUpd = dv.getUint32(8, true);
  const remOff = 12 + nUpd * RECORD_BYTES;
  if (dv.byteLength < remOff + 4) throw new FormatError(`OSPD: truncated before the removed count (${nUpd} updates)`);
  const updated = readRecords(dv, 12, nUpd);
  const nRem = dv.getUint32(remOff, true);
  const end = remOff + 4 + nRem * 4;
  if (dv.byteLength < end) throw new FormatError(`OSPD: truncated, ${dv.byteLength} < ${end} bytes`);
  const removed = new Uint32Array(nRem);
  for (let i = 0; i < nRem; i++) removed[i] = dv.getUint32(remOff + 4 + i * 4, true);
  return { updated, removed };
}

// encodeOSPD is the inverse of parseOSPD (used by tests and tooling).
export function encodeOSPD(updated: SplatCloud, removed: ArrayLike<number>): Uint8Array {
  const out = new Uint8Array(12 + updated.count * RECORD_BYTES + 4 + removed.length * 4);
  const dv = new DataView(out.buffer);
  writeMagic(dv, 0, OSPD_MAGIC);
  dv.setUint32(4, FORMAT_VERSION, true);
  dv.setUint32(8, updated.count, true);
  writeRecords(dv, 12, updated);
  const remOff = 12 + updated.count * RECORD_BYTES;
  dv.setUint32(remOff, removed.length, true);
  for (let i = 0; i < removed.length; i++) dv.setUint32(remOff + 4 + i * 4, removed[i], true);
  return out;
}

// copyGaussian copies record si of src into slot di of dst.
function copyGaussian(dst: SplatCloud, di: number, src: SplatCloud, si: number): void {
  dst.ids[di] = src.ids[si];
  for (let k = 0; k < 3; k++) {
    dst.pos[di * 3 + k] = src.pos[si * 3 + k];
    dst.logScale[di * 3 + k] = src.logScale[si * 3 + k];
    dst.color[di * 3 + k] = src.color[si * 3 + k];
  }
  for (let k = 0; k < 4; k++) dst.rot[di * 4 + k] = src.rot[si * 4 + k];
  dst.opacity[di] = src.opacity[si];
  dst.label[di] = src.label[si];
  const dm = dst.material;
  if (!dm) return;
  const sm = src.material;
  if (sm) {
    for (let k = 0; k < 3; k++) {
      dm.albedo[di * 3 + k] = sm.albedo[si * 3 + k];
      dm.normal[di * 3 + k] = sm.normal[si * 3 + k];
    }
    dm.ao[di] = sm.ao[si];
    dm.roughness[di] = sm.roughness[si];
    dm.specular[di] = sm.specular[si];
    return;
  }
  // A v1 record (e.g. an OSPD update) landing in a v2 cloud: the defaults a
  // v2 reader gives v1 data (albedo from the displayed colour, ao 1,
  // roughness 1, specular 0, normal +Z).
  for (let k = 0; k < 3; k++) dm.albedo[di * 3 + k] = srgbToLinear(src.color[si * 3 + k]);
  dm.ao[di] = 1;
  dm.roughness[di] = 1;
  dm.specular[di] = 0;
  dm.normal[di * 3] = 0;
  dm.normal[di * 3 + 1] = 0;
  dm.normal[di * 3 + 2] = 1;
}

// srgbToLinear is the sRGB EOTF on 0..1.
export function srgbToLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

// emptyLike is an empty cloud of n records with a material iff c has one.
function emptyLike(c: SplatCloud, n: number): SplatCloud {
  const out = emptyCloud(n);
  if (c.material) out.material = emptyMaterial(n);
  return out;
}

// applyDelta returns a new cloud: removed ids dropped, updated ids replaced in
// place, unknown updated ids appended (in delta order).
export function applyDelta(base: SplatCloud, delta: SplatDelta): SplatCloud {
  const removed = new Set<number>(delta.removed);
  const updIndex = new Map<number, number>();
  for (let i = 0; i < delta.updated.count; i++) updIndex.set(delta.updated.ids[i], i);

  let keep = 0;
  for (let i = 0; i < base.count; i++) if (!removed.has(base.ids[i])) keep++;
  const seen = new Set<number>();
  let appended = 0;
  for (let i = 0; i < base.count; i++) if (!removed.has(base.ids[i])) seen.add(base.ids[i]);
  for (let i = 0; i < delta.updated.count; i++) if (!seen.has(delta.updated.ids[i])) appended++;

  const out = emptyLike(base, keep + appended);
  let w = 0;
  for (let i = 0; i < base.count; i++) {
    const id = base.ids[i];
    if (removed.has(id)) continue;
    const u = updIndex.get(id);
    if (u !== undefined) copyGaussian(out, w, delta.updated, u);
    else copyGaussian(out, w, base, i);
    w++;
  }
  for (let i = 0; i < delta.updated.count; i++) {
    if (seen.has(delta.updated.ids[i])) continue;
    copyGaussian(out, w++, delta.updated, i);
  }
  return out;
}

// filterLabels returns the subset whose label is in `labels` (empty = all).
export function filterLabels(c: SplatCloud, labels: readonly number[]): SplatCloud {
  if (labels.length === 0) return c;
  const want = new Set(labels);
  let n = 0;
  for (let i = 0; i < c.count; i++) if (want.has(c.label[i])) n++;
  const out = emptyLike(c, n);
  let w = 0;
  for (let i = 0; i < c.count; i++) if (want.has(c.label[i])) copyGaussian(out, w++, c, i);
  return out;
}

// cloudFromGaussians builds a SoA cloud from records.
export function cloudFromGaussians(gs: readonly Gaussian[]): SplatCloud {
  const c = emptyCloud(gs.length);
  gs.forEach((g, i) => {
    c.ids[i] = g.id;
    c.pos.set(g.pos, i * 3);
    c.logScale.set(g.logScale, i * 3);
    c.rot.set(g.rot, i * 4);
    c.color.set(g.color, i * 3);
    c.opacity[i] = g.opacity;
    c.label[i] = g.label;
  });
  return c;
}

// gaussianAt reads record i back as a struct.
export function gaussianAt(c: SplatCloud, i: number): Gaussian {
  return {
    id: c.ids[i],
    pos: [c.pos[i * 3], c.pos[i * 3 + 1], c.pos[i * 3 + 2]],
    logScale: [c.logScale[i * 3], c.logScale[i * 3 + 1], c.logScale[i * 3 + 2]],
    rot: [c.rot[i * 4], c.rot[i * 4 + 1], c.rot[i * 4 + 2], c.rot[i * 4 + 3]],
    color: [c.color[i * 3], c.color[i * 3 + 1], c.color[i * 3 + 2]],
    opacity: c.opacity[i],
    label: c.label[i],
  };
}

// cloudBounds returns the [min,max] box over gaussians with the given labels
// (empty = all), trimming the `trim` fraction of outliers on each axis.
export function cloudBounds(
  c: SplatCloud,
  labels: readonly number[] = [],
  trim = 0.005,
): { min: [number, number, number]; max: [number, number, number] } | null {
  const want = labels.length ? new Set(labels) : null;
  const axes: number[][] = [[], [], []];
  for (let i = 0; i < c.count; i++) {
    if (want && !want.has(c.label[i])) continue;
    for (let k = 0; k < 3; k++) axes[k].push(c.pos[i * 3 + k]);
  }
  if (axes[0].length === 0) return null;
  const min: [number, number, number] = [0, 0, 0];
  const max: [number, number, number] = [0, 0, 0];
  for (let k = 0; k < 3; k++) {
    const a = axes[k].sort((x, y) => x - y);
    const lo = Math.min(a.length - 1, Math.floor(a.length * trim));
    const hi = Math.max(0, Math.ceil(a.length * (1 - trim)) - 1);
    min[k] = a[lo];
    max[k] = a[Math.max(lo, hi)];
  }
  return { min, max };
}
